import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import * as pdfjsLib from "pdfjs-dist";
import SignaturePad from "./SignatureCanvas";
import FieldInputModal from "./FieldInputModal";
import ConfirmModal from "./ConfirmModal";
import { esignatureService } from "../../services/esignature";
import "./SigningPage.css";

// Worker is configured globally in index.js - no need to set it here

// Scale fields are read-only placeholders resolved outside of signing.
const SCALE_FIELD_LABELS = {
  commission_scale: "Commission Scale",
  override_scale: "Override Scale",
  md_scale: "Marketing Deal",
};
const isScaleField = fieldType =>
  Object.prototype.hasOwnProperty.call(SCALE_FIELD_LABELS, fieldType);

// Target on-screen page width: US Letter at 96dpi (8.5in). Each page is
// rendered at its native resolution and then scaled to this width (aspect ratio
// preserved) so the document reads at a sensible, fixed size.
const LETTER_DISPLAY_WIDTH = 816;

// The PDF render scale used both here and in the template editor (PDFViewer).
// Field coordinates are stored in the editor's canvas space (page size × this
// scale), so they're re-projected onto each rendered page below.
const RENDER_SCALE = 1.5;

// Returns the width (px) the PDF should fill, capped at LETTER_DISPLAY_WIDTH.
// Uses window.innerWidth directly so the correct value is available on the
// very first render — before any ResizeObserver or layout measurement can fire.
function computePdfDisplayWidth() {
  const isMobile = window.innerWidth <= 768;
  // Desktop: subtract the 320px sidebar + 1px border + scroll padding.
  // Mobile: sidebar moves to bottom so the full width is available minus padding.
  const reserved = isMobile ? 10 : 341; // 320 sidebar + 1 border + 20 padding
  return Math.min(LETTER_DISPLAY_WIDTH, window.innerWidth - reserved);
}

const SigningPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [documentData, setDocumentData] = useState(null);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [showFieldInput, setShowFieldInput] = useState(false);
  const [currentField, setCurrentField] = useState(null);
  const [signedFields, setSignedFields] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [consentAgreed, setConsentAgreed] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageCanvases, setPageCanvases] = useState([]);
  const [modal, setModal] = useState(null);
  const [displayWidth, setDisplayWidth] = useState(() =>
    computePdfDisplayWidth()
  );
  const pageRefs = useRef([]);
  const pdfScrollRef = useRef(null);

  // Recompute PDF display width on window resize.
  useEffect(() => {
    const handleResize = () => setDisplayWidth(computePdfDisplayWidth());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const closeModal = () => setModal(null);

  // Simple alert replacement: a single-button informational modal.
  const showAlert = (title, message) =>
    setModal({ title, message, confirmText: "OK", cancelText: null });

  // Confirm replacement: runs `onConfirm` when the user accepts.
  const showConfirm = ({ title, message, confirmText, danger, onConfirm }) =>
    setModal({
      title,
      message,
      confirmText,
      cancelText: "Cancel",
      danger,
      onConfirm,
    });

  useEffect(() => {
    loadDocument();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Auto-mark scale fields as complete when document loads (they're read-only)
  useEffect(() => {
    if (documentData?.fields) {
      const scaleFields = documentData.fields.filter(field =>
        isScaleField(field.field_type)
      );

      if (scaleFields.length > 0) {
        const autoCompleteFields = {};
        scaleFields.forEach(field => {
          autoCompleteFields[field.id] = {
            data: "read-only-scale",
            type: field.field_type,
          };
        });
        setSignedFields(prev => ({ ...prev, ...autoCompleteFields }));
      }
    }
  }, [documentData]);

  const loadDocument = async () => {
    try {
      setLoading(true);
      const response = await esignatureService.getDocumentByToken(token);
      // Unwrap the data from the API response
      setDocumentData(response.data || response);
    } catch (error) {
      showAlert("Invalid Link", "This signing link is invalid or has expired.");
    } finally {
      setLoading(false);
    }
  };

  // Load and render PDF
  useEffect(() => {
    if (!documentData?.document?.storage_path) {
      return;
    }

    const loadAndRenderPdf = async () => {
      try {
        const loadingTask = pdfjsLib.getDocument({
          url: documentData.document.storage_path,
          cMapUrl: `${window.location.protocol}//${window.location.host}${process.env.PUBLIC_URL || ""}/cmaps/`,
          cMapPacked: true,
        });

        // loadingTask.onProgress = progress => {
        //   console.log(
        //     "SigningPage: Loading progress:",
        //     progress.loaded,
        //     "/",
        //     progress.total
        //   );
        // };

        const pdf = await loadingTask.promise;

        setPdfDoc(pdf);

        // Render all pages
        const canvases = [];
        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          const page = await pdf.getPage(pageNum);
          const viewport = page.getViewport({ scale: RENDER_SCALE });

          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");
          canvas.width = viewport.width;
          canvas.height = viewport.height;

          await page.render({
            canvasContext: context,
            viewport: viewport,
          }).promise;

          canvases.push({
            pageNum,
            canvas,
            width: viewport.width,
            height: viewport.height,
          });
        }

        setPageCanvases(canvases);
      } catch (error) {
        showAlert("Error", "Failed to load the PDF document.");
      }
    };

    loadAndRenderPdf();

    return () => {
      if (pdfDoc) {
        pdfDoc.destroy();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentData]);

  const openFieldEditor = field => {
    setCurrentField(field);

    if (field.field_type === "signature" || field.field_type === "initial") {
      setShowSignaturePad(true);
    } else if (field.field_type === "checkbox") {
      // Toggle checkbox directly without modal
      handleCheckboxToggle(field);
    } else if (
      field.field_type === "text" ||
      field.field_type === "email" ||
      field.field_type === "phone" ||
      field.field_type === "date"
    ) {
      setShowFieldInput(true);
    }
  };

  const handleFieldClick = field => {
    // Scale fields are read-only, do not allow interaction
    if (isScaleField(field.field_type)) {
      return;
    }

    if (signedFields[field.id]) {
      showConfirm({
        title: "Update Field",
        message: "This field is already filled. Do you want to update it?",
        confirmText: "Update",
        onConfirm: () => openFieldEditor(field),
      });
      return;
    }

    openFieldEditor(field);
  };

  const handleCheckboxToggle = field => {
    const newValue = !signedFields[field.id]?.data;
    setSignedFields({
      ...signedFields,
      [field.id]: {
        data: newValue,
        type: field.field_type,
      },
    });
    setCurrentField(null);
  };

  const handleFieldInputSave = inputData => {
    if (!currentField) return;

    setSignedFields({
      ...signedFields,
      [currentField.id]: {
        data: inputData,
        type: currentField.field_type,
      },
    });

    setShowFieldInput(false);
    setCurrentField(null);
  };

  const handleSignatureSave = signatureData => {
    if (!currentField) return;

    setSignedFields({
      ...signedFields,
      [currentField.id]: {
        data: signatureData,
        type: currentField.field_type,
      },
    });

    setShowSignaturePad(false);
    setCurrentField(null);
  };

  const checkAllRequiredFieldsFilled = () => {
    if (!documentData?.fields) return false;
    const requiredFields = documentData.fields.filter(f => f.required);
    return requiredFields.every(f => signedFields[f.id]);
  };

  const handleSubmitSignatures = () => {
    if (!checkAllRequiredFieldsFilled()) {
      showAlert(
        "Incomplete",
        "Please complete all required fields before submitting."
      );
      return;
    }

    if (!consentAgreed) {
      showAlert(
        "Consent Required",
        "Please agree to use electronic records and signatures before submitting."
      );
      return;
    }

    showConfirm({
      title: "Submit Signatures",
      message:
        "Are you sure you want to submit your signatures? This action cannot be undone.",
      confirmText: "Submit",
      onConfirm: submitSignatures,
    });
  };

  const submitSignatures = async () => {
    try {
      setSubmitting(true);
      let redirectUrl = null;

      // The backend requires electronic-records consent to be recorded before
      // any field can be signed.
      await esignatureService.acceptConsent(token);

      for (const [fieldId, fieldData] of Object.entries(signedFields)) {
        const payload = {};
        if (fieldData.type === "signature" || fieldData.type === "initial") {
          payload.signature_data = fieldData.data;
        } else {
          payload.text_value = fieldData.data;
        }
        const response = await esignatureService.submitSignature(
          token,
          fieldId,
          payload
        );

        // Check if any field submission returns a redirect URL (for Direct Seller conversion)
        if (response?.data?.redirect_url) {
          redirectUrl = response.data.redirect_url;
        }
      }

      setCompleted(true);

      // If a redirect URL was provided (Direct Seller conversion), redirect after a brief delay
      if (redirectUrl) {
        setTimeout(() => {
          window.location.href = redirectUrl;
        }, 2000); // 2 second delay to show success message
      }
    } catch (error) {
      showAlert(
        "Submission Failed",
        "Failed to submit signatures. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="signing-page loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!documentData) {
    return (
      <div className="signing-page error">
        <div className="error-message">
          <h2>Document Not Found</h2>
          <p>This signing link is invalid or has expired.</p>
        </div>
      </div>
    );
  }

  // Check if recipient has already signed (from backend)
  if (documentData?.recipient?.status === "signed" || completed) {
    return (
      <div className="signing-page success">
        <div className="success-message">
          <div className="success-icon">✓</div>
          <h2>Document Signed Successfully!</h2>
          <p>Thank you, {documentData.recipient.full_name}</p>
          <p className="detail">
            {completed
              ? "Your signatures have been submitted successfully. Please wait while we complete your onboarding..."
              : "You have already completed signing this document."}
            {!completed && documentData.document?.status === "completed"
              ? " All parties have signed and the document is fully executed."
              : !completed
                ? " Waiting for other signers to complete."
                : ""}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="signing-page">
      <div className="signing-header">
        <h1>{documentData?.document?.title || "Document"}</h1>
        <p className="signer-info">
          Signing as: {documentData?.recipient?.full_name || "Signer"}
        </p>
      </div>

      <div className="signing-body">
        <div className="pdf-container">
          <div className="pdf-viewer-scroll" ref={pdfScrollRef}>
            <div style={{ position: "relative", display: "inline-block" }}>
              {/* Render all pages */}
              {pageCanvases.map((pageData, index) => {
                const scale = displayWidth / pageData.width;
                return (
                  <div
                    key={pageData.pageNum}
                    style={{
                      position: "relative",
                      width: `${pageData.width * scale}px`,
                      height: `${pageData.height * scale}px`,
                      marginBottom:
                        index < pageCanvases.length - 1 ? "20px" : "0",
                    }}
                    ref={el => (pageRefs.current[index] = el)}
                  >
                    <div
                      style={{
                        position: "relative",
                        width: `${pageData.width}px`,
                        height: `${pageData.height}px`,
                        transform: `scale(${scale})`,
                        transformOrigin: "top left",
                      }}
                    >
                      <canvas
                        width={pageData.width}
                        height={pageData.height}
                        ref={canvas => {
                          if (canvas && pageData.canvas) {
                            const ctx = canvas.getContext("2d");
                            ctx.drawImage(pageData.canvas, 0, 0);
                          }
                        }}
                        style={{ display: "block" }}
                      />

                      {/* Render fields for this page */}
                      {documentData?.fields
                        ?.filter(f => f.page_number === pageData.pageNum)
                        ?.map(field => {
                          const fieldIsScale = isScaleField(field.field_type);

                          // Field coordinates AND page_width/page_height are in
                          // PDF points (the page's natural size). Express each
                          // coordinate as a fraction of the page in points, then
                          // re-project onto this rendered page (pageData is the
                          // page rendered at RENDER_SCALE). This lines fields up
                          // regardless of render scale or page size.
                          const pageWidthPts =
                            field.page_width || pageData.width / RENDER_SCALE;
                          const pageHeightPts =
                            field.page_height || pageData.height / RENDER_SCALE;
                          const leftPx =
                            (field.x_position / pageWidthPts) * pageData.width;
                          const topPx =
                            (field.y_position / pageHeightPts) *
                            pageData.height;
                          const widthPx =
                            (field.width / pageWidthPts) * pageData.width;
                          const heightPx =
                            (field.height / pageHeightPts) * pageData.height;

                          return (
                            <React.Fragment key={field.id}>
                              <div
                                className={`signing-field ${signedFields[field.id] ? "signed" : "unsigned"} ${field.required ? "required" : ""} ${field.field_type === "checkbox" ? "checkbox-field" : ""} ${fieldIsScale ? "scale-field" : ""}`}
                                onClick={() => handleFieldClick(field)}
                                style={{
                                  position: "absolute",
                                  left: `${leftPx}px`,
                                  top: `${topPx}px`,
                                  width: `${widthPx}px`,
                                  height: `${heightPx}px`,
                                  pointerEvents: fieldIsScale ? "none" : "auto",
                                }}
                              >
                                {fieldIsScale ? (
                                  <div
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      padding: "12px",
                                      backgroundColor: "#f8f9fa",
                                      border: "2px dashed #999",
                                      borderRadius: "4px",
                                      textAlign: "center",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        fontWeight: "600",
                                        color: "#333",
                                      }}
                                    >
                                      {SCALE_FIELD_LABELS[field.field_type]}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "10px",
                                        color: "#666",
                                        marginTop: "4px",
                                      }}
                                    >
                                      View Only
                                    </div>
                                  </div>
                                ) : signedFields[field.id] ? (
                                  field.field_type === "signature" ||
                                  field.field_type === "initial" ? (
                                    <img
                                      src={signedFields[field.id].data}
                                      alt="Signature"
                                    />
                                  ) : field.field_type === "checkbox" ? (
                                    <div className="checkbox-checked">
                                      <span className="checkmark">✓</span>
                                    </div>
                                  ) : (
                                    <span className="field-value">
                                      {signedFields[field.id].data}
                                    </span>
                                  )
                                ) : (
                                  <span className="placeholder">
                                    {field.field_type === "checkbox"
                                      ? "☐"
                                      : `Click to ${field.field_type}`}
                                    {field.required &&
                                      field.field_type !== "checkbox" &&
                                      " *"}
                                  </span>
                                )}
                              </div>
                            </React.Fragment>
                          );
                        })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="signing-sidebar">
          <h3>Required Fields</h3>
          <div className="fields-list">
            {documentData?.fields?.map(field => (
              <div
                key={field.id}
                className={`field-item ${signedFields[field.id] ? "completed" : "pending"}`}
                onClick={() => {
                  const pageIndex = field.page_number - 1;
                  if (pageRefs.current[pageIndex]) {
                    pageRefs.current[pageIndex].scrollIntoView({
                      behavior: "smooth",
                      block: "center",
                    });
                    setTimeout(() => handleFieldClick(field), 500);
                  }
                }}
              >
                <div className="field-icon">
                  {signedFields[field.id] ? "✓" : field.required ? "!" : "○"}
                </div>
                <div className="field-details">
                  <div className="field-label">{field.label}</div>
                  <div className="field-meta">
                    Page {field.page_number} • {field.field_type}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="submit-section">
            <label className="consent-disclosure">
              <input
                type="checkbox"
                checked={consentAgreed}
                onChange={e => setConsentAgreed(e.target.checked)}
                disabled={submitting}
              />
              <span>
                I agree to use electronic records and signatures, and I consent
                to sign this document electronically.
              </span>
            </label>
            <button
              className="submit-button"
              onClick={handleSubmitSignatures}
              disabled={
                !checkAllRequiredFieldsFilled() || !consentAgreed || submitting
              }
            >
              {submitting ? "Submitting..." : "Submit Signatures"}
            </button>
            {!checkAllRequiredFieldsFilled() ? (
              <p className="submit-hint">
                Complete all required fields to enable submit
              </p>
            ) : (
              !consentAgreed && (
                <p className="submit-hint">
                  Agree to electronic records and signatures to enable submit
                </p>
              )
            )}
          </div>
        </div>
      </div>

      {showSignaturePad && (
        <SignaturePad
          onSave={handleSignatureSave}
          onCancel={() => {
            setShowSignaturePad(false);
            setCurrentField(null);
          }}
        />
      )}

      {showFieldInput && currentField && (
        <FieldInputModal
          field={currentField}
          initialValue={signedFields[currentField.id]?.data || ""}
          onSave={handleFieldInputSave}
          onCancel={() => {
            setShowFieldInput(false);
            setCurrentField(null);
          }}
        />
      )}

      {modal && (
        <ConfirmModal
          title={modal.title}
          message={modal.message}
          confirmText={modal.confirmText}
          cancelText={modal.cancelText}
          danger={modal.danger}
          onConfirm={() => {
            const onConfirm = modal.onConfirm;
            closeModal();
            if (onConfirm) onConfirm();
          }}
          onCancel={closeModal}
        />
      )}
    </div>
  );
};

export default SigningPage;
